import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAssets, createAsset, updateAsset, deleteAsset, getEmployees, getErrorMessage } from "../api";
import { requiredTrimmed } from "../utils/validate";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";

const NAVY = "#1e3a8a";

const TYPE_ICONS: Record<string, string> = {
  laptop: "💻", phone: "📱", monitor: "🖥️", tablet: "📟",
  keyboard: "⌨️", mouse: "🖱️", headset: "🎧", other: "📦",
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  available:   { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  assigned:    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  maintenance: { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  retired:     { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" },
};

interface Asset {
  id: number;
  name: string;
  asset_type: string;
  brand: string | null;
  model_name: string | null;
  serial_number: string | null;
  purchase_date: string | null;
  purchase_cost: number | null;
  assigned_to: number | null;
  assignee_name: string | null;
  assignee_email: string | null;
  assigned_date: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

interface Employee { id: number; name: string; email: string; }

const ASSET_TYPES = ["laptop", "phone", "monitor", "tablet", "keyboard", "mouse", "headset", "other"];
const ASSET_STATUSES = ["available", "assigned", "maintenance", "retired"];

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0",
  borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box",
};

function Field({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <div>
      <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function AssetModal({ asset, onClose, employees }: {
  asset: Asset | null;
  onClose: () => void;
  employees: Employee[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: asset?.name || "",
    asset_type: asset?.asset_type || "laptop",
    brand: asset?.brand || "",
    model_name: asset?.model_name || "",
    serial_number: asset?.serial_number || "",
    purchase_date: asset?.purchase_date || "",
    purchase_cost: asset?.purchase_cost?.toString() || "",
    notes: asset?.notes || "",
    status: asset?.status || "available",
  });
  const [assignTo, setAssignTo] = useState<string>(asset?.assigned_to?.toString() || "");
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createAsset,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assets"] }); onClose(); },
    onError: (e: any) => toast(getErrorMessage(e, "Failed to save"), "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: any) => updateAsset(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["assets"] }); onClose(); },
    onError: (e: any) => toast(getErrorMessage(e, "Failed to save"), "error"),
  });

  async function handleSave() {
    const err = requiredTrimmed(form.name, "Asset name");
    setNameError(err);
    if (err) return;
    setSaving(true);
    try {
      const payload: any = {
        ...form,
        name: form.name.trim(),
        purchase_cost: form.purchase_cost ? parseFloat(form.purchase_cost) : null,
      };
      if (assignTo) payload.assigned_to = parseInt(assignTo);
      if (!asset) {
        delete payload.status;
        await createMut.mutateAsync(payload);
      } else {
        // Only send status if the admin explicitly changed it — otherwise let the
        // backend keep deriving it from assignment (e.g. Assign To -> "assigned").
        if (form.status === asset.status) delete payload.status;
        // Setting Assign To back to "— Unassigned —" has to be sent as an explicit
        // unassign: omitting assigned_to reads to the backend as "leave assignment
        // alone", so the asset would silently stay assigned. Only when it was
        // actually assigned before — an unassigned asset would otherwise have its
        // status reset to "available" on every unrelated edit.
        if (!assignTo && asset.assigned_to) payload.unassign = true;
        await updateMut.mutateAsync({ id: asset.id, data: payload });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <LoadingOverlay active={saving} />
      <div className="modal-sheet" style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            {asset ? "Edit Asset" : "Add Asset"}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", position: "relative", zIndex: 10 }}>✕</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Asset Name *">
              <input value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setNameError(null); }}
                placeholder="e.g. MacBook Pro 14" style={inputStyle} />
              {nameError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{nameError}</div>}
            </Field>
          </div>
          <Field label="Type">
            <select value={form.asset_type} onChange={e => setForm(f => ({ ...f, asset_type: e.target.value }))} style={inputStyle}>
              {ASSET_TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Brand">
            <input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
              placeholder="Apple, Dell, HP…" style={inputStyle} />
          </Field>
          <Field label="Model">
            <input value={form.model_name} onChange={e => setForm(f => ({ ...f, model_name: e.target.value }))}
              placeholder="Model name/number" style={inputStyle} />
          </Field>
          <Field label="Serial Number">
            <input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))}
              placeholder="S/N" style={inputStyle} />
          </Field>
          <Field label="Purchase Date">
            <DateInput value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} style={inputStyle} />
          </Field>
          <Field label="Purchase Cost">
            <input type="number" value={form.purchase_cost} onChange={e => setForm(f => ({ ...f, purchase_cost: e.target.value }))}
              placeholder="0.00" style={inputStyle} />
          </Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Assign To">
              <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={inputStyle}>
                <option value="">— Unassigned —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.email})</option>)}
              </select>
            </Field>
          </div>
          {asset && (
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Status">
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                  {ASSET_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </Field>
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Notes">
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Any additional notes…" />
            </Field>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "10px 24px", background: NAVY, color: "white", border: "none",
            borderRadius: 8, fontWeight: 700, cursor: "pointer",
            fontSize: 13,
          }}>{saving ? "Saving…" : "Save"}</button>
          <button onClick={onClose} style={{
            padding: "10px 20px", background: "white", color: "#64748b",
            border: "1.5px solid #e2e8f0", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13,
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "white", borderRadius: 12, padding: 28, maxWidth: 360, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <p style={{ margin: "0 0 20px", fontSize: 15, color: "#0f172a", fontWeight: 500 }}>{message}</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "8px 18px", background: "white", color: "#64748b", border: "1.5px solid #e2e8f0", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: "8px 18px", background: "#ef4444", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

export default function AssetsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isHR = user?.role === "admin" || user?.role === "admin_assistant";
  const [modal, setModal] = useState<Asset | null | "new">(null);
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const { data: assets = [], isLoading } = useQuery({ queryKey: ["assets"], queryFn: () => getAssets() });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees, enabled: isHR });

  const deleteMut = useMutation({
    mutationFn: deleteAsset,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assets"] }),
  });
  const unassignMut = useMutation({
    mutationFn: ({ id }: { id: number }) => updateAsset(id, { unassign: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assets"] }),
  });

  const all = assets as Asset[];
  const filtered = all.filter(a =>
    (!statusFilter || a.status === statusFilter) &&
    (!search || a.name.toLowerCase().includes(search.toLowerCase()) ||
      (a.assignee_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.serial_number || "").toLowerCase().includes(search.toLowerCase()))
  );

  const counts = { available: 0, assigned: 0, maintenance: 0, retired: 0 };
  all.forEach(a => { if (a.status in counts) (counts as any)[a.status]++; });

  return (
    <div>
      <LoadingOverlay active={deleteMut.isPending || unassignMut.isPending} />
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>Assets</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
            {all.length} assets · {counts.assigned} assigned · {counts.available} available
          </p>
        </div>
        {isHR && (
          <button onClick={() => setModal("new")} style={{
            padding: "10px 20px", background: NAVY, color: "white", border: "none",
            borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14,
          }}>+ Add Asset</button>
        )}
      </div>

      {/* Filters */}
      {isHR && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          {(["", "available", "assigned", "maintenance", "retired"] as const).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
              background: statusFilter === s ? NAVY : "white",
              color: statusFilter === s ? "white" : "#64748b",
              border: `1.5px solid ${statusFilter === s ? NAVY : "#e2e8f0"}`,
            }}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}{s && counts[s as keyof typeof counts] !== undefined ? ` (${counts[s as keyof typeof counts]})` : ""}</button>
          ))}
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, assignee, S/N…"
            style={{ flex: "1 1 240px", padding: "7px 14px", border: "1.5px solid #e2e8f0", borderRadius: 20, fontSize: 13, outline: "none" }} />
        </div>
      )}

      {isLoading ? (
        <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 80, color: "#94a3b8" }}>
          <div style={{ fontSize: 48 }}>📦</div>
          <div style={{ marginTop: 12 }}>{all.length === 0 ? (isHR ? "No assets yet. Add your first asset." : "No assets have been assigned to you yet.") : "No assets match your filter."}</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {filtered.map(a => {
            const sc = STATUS_COLORS[a.status] || STATUS_COLORS.available;
            return (
              <div key={a.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 24 }}>{TYPE_ICONS[a.asset_type] || "📦"}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{a.name}</div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>{[a.brand, a.model_name].filter(Boolean).join(" · ") || a.asset_type}</div>
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 10, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, height: "fit-content" }}>
                    {a.status}
                  </span>
                </div>

                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
                  {a.serial_number && <div>S/N: <b>{a.serial_number}</b></div>}
                  {a.assignee_name ? (
                    <div style={{ color: "#1d4ed8" }}>Assigned to: <b>{a.assignee_name}</b>{a.assigned_date ? ` since ${formatDate(a.assigned_date)}` : ""}</div>
                  ) : (
                    <div style={{ color: "#166534" }}>Available for assignment</div>
                  )}
                  {a.purchase_cost != null && <div>Cost: <b>{a.purchase_cost.toLocaleString()}</b></div>}
                </div>

                {isHR && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => setModal(a)} style={{
                      flex: 1, padding: "6px 12px", background: "#f8fafc", color: "#475569",
                      border: "1.5px solid #e2e8f0", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>Edit</button>
                    {a.assigned_to && (
                      <button onClick={() => setConfirmAction({ message: "Unassign this asset?", onConfirm: () => unassignMut.mutate({ id: a.id }) })} style={{
                        flex: 1, padding: "6px 12px", background: "#fef9c3", color: "#854d0e",
                        border: "1.5px solid #fde047", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}>Unassign</button>
                    )}
                    <button onClick={() => setConfirmAction({ message: "Delete this asset?", onConfirm: () => deleteMut.mutate(a.id) })} style={{
                      padding: "6px 10px", background: "#fef2f2", color: "#991b1b",
                      border: "1.5px solid #fca5a5", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>✕</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <AssetModal
          asset={modal === "new" ? null : modal as Asset}
          onClose={() => setModal(null)}
          employees={employees as Employee[]}
        />
      )}
      {confirmAction && (
        <ConfirmModal
          message={confirmAction.message}
          onConfirm={() => { confirmAction.onConfirm(); setConfirmAction(null); }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
